import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import authService from '../services/authService';
import api from '../services/api';
import { PERMISSIONS } from '../config/permissions';

const AuthContext = createContext(null);

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes (HIPAA AC-03)
const WARNING_LEAD_MS = 2 * 60 * 1000; // Show warning 2 minutes before logout

/**
 * GH-79: the server returns user state in two shapes depending on the path:
 *   - login / cognitoLogin / /auth/me → wrapper { user: {firstName, lastName, phone, ...}, practiceGroupIds, activeContext, ... }
 *   - SSO loginWithToken → flat { id, email, first_name, last_name, role, ... }
 *
 * Components expect a flat shape (PlatformHeader reads user?.first_name,
 * UserProfile reads user.firstName/user.first_name). Without normalization,
 * a profile save → refreshUser → /auth/me returns the wrapper → setUser
 * replaces the flat one → fields appear blank → user sees the update as
 * "lost".
 *
 * Normalize to a flat user with both casings + keep context fields beside it.
 */
function normalizeUser(raw) {
  if (!raw) return null;
  // Already flat (SSO path)
  if ((raw.firstName !== undefined || raw.first_name !== undefined) && !raw.user) return raw;
  // Wrapper path — promote inner user fields to top level
  const inner = raw.user && typeof raw.user === 'object' ? raw.user : raw;
  return {
    id: inner.id,
    email: inner.email,
    role: inner.role,
    firstName: inner.firstName ?? inner.first_name,
    lastName: inner.lastName ?? inner.last_name,
    first_name: inner.first_name ?? inner.firstName,
    last_name: inner.last_name ?? inner.lastName,
    phone: inner.phone ?? null,
    // Preserve context fields for hasRole / isSuperAdmin / isPracticeAdmin
    user: inner,
    practiceGroupIds: raw.practiceGroupIds,
    practices: raw.practices,
    activeContext: raw.activeContext,
    isSuperAdmin: raw.isSuperAdmin,
    availableContexts: raw.availableContexts,
  };
}

export function AuthProvider({ children }) {
  const [userRaw, setUserRaw] = useState(null);
  const user = userRaw; // already normalized via setUser wrapper below
  const setUser = (raw) => setUserRaw(normalizeUser(raw));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  // Context selection state (two-step login)
  const [pendingContextSelection, setPendingContextSelection] = useState(null);

  // Inactivity timer refs
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  // Logout function
  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setPendingContextSelection(null);
    setShowTimeoutWarning(false);
  }, []);

  // Dismiss warning and reset timers (user clicked "Stay Signed In")
  const dismissTimeoutWarning = useCallback(() => {
    setShowTimeoutWarning(false);
  }, []);

  // Inactivity timeout — warning at 13 min, logout at 15 min idle
  const resetInactivityTimer = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    setShowTimeoutWarning(false);

    warningTimerRef.current = setTimeout(() => {
      if (authService.isAuthenticated()) {
        setShowTimeoutWarning(true);
      }
    }, INACTIVITY_TIMEOUT_MS - WARNING_LEAD_MS);

    inactivityTimerRef.current = setTimeout(() => {
      if (authService.isAuthenticated()) {
        logout();
        window.__sessionExpired = true;
      }
    }, INACTIVITY_TIMEOUT_MS);
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    const handler = () => resetInactivityTimer();

    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetInactivityTimer(); // start the timer

    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [isAuthenticated, resetInactivityTimer]);

  // Set up API auth error handler
  useEffect(() => {
    api.onAuthError = logout;
    return () => {
      api.onAuthError = null;
    };
  }, [logout]);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);

      // First, check if setup is required
      const setupStatus = await authService.checkSetupStatus();
      if (setupStatus.setupRequired) {
        setSetupRequired(true);
        setIsLoading(false);
        return;
      }

      // Then check if user is authenticated
      if (authService.isAuthenticated()) {
        let result = await authService.validateToken();

        // A transient failure (network drop, aborted request, gateway hiccup) is not
        // the token being invalid. Give it one more try before deciding anything.
        if (!result.success && result.transient) {
          await new Promise((r) => setTimeout(r, 1500));
          result = await authService.validateToken();
        }

        if (result.success) {
          setUser(result.user);
          setIsAuthenticated(true);
        } else if (result.transient) {
          // Still could not reach the server. Do NOT log out: that deletes the
          // token, and the token is not what failed. Leave the session in place so
          // a reload once the connection recovers restores it without a new login.
          // This used to call logout() here, which is how a quick navigation during
          // the check threw users to the login screen with their session destroyed.
          console.warn('[Auth] Could not validate the session (transient); keeping the token.', result.error);
          setIsAuthenticated(false);
        } else {
          logout();
        }
      }

      setIsLoading(false);
    };

    checkAuth();
  }, [logout]);

  // Login function — handles two-step flow
  const login = async (email, password) => {
    const result = await authService.login(email, password);

    if (result.success) {
      // MFA required — return challenge to Login page
      if (result.mfaRequired) {
        return { success: true, mfaRequired: true, mfaToken: result.mfaToken, user: result.user };
      }

      // Two-step login: context selection needed
      if (result.needsContextSelection) {
        setPendingContextSelection({
          contexts: result.contexts,
          user: result.user
        });
        return { success: true, needsContextSelection: true };
      }

      // Direct login (super admin or single context)
      setUser(result.user);
      setIsAuthenticated(true);
      return { success: true };
    }

    return { success: false, error: result.error, newPasswordRequired: result.newPasswordRequired, cognitoUser: result.cognitoUser };
  };

  // SSO login — receive pre-generated JWT from SAML callback
  const loginWithToken = (token) => {
    localStorage.setItem('nh_auth_token', token);
    // Decode token payload to get user info (without verification — server already verified)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUser({
        id: payload.userId,
        email: payload.email,
        first_name: payload.firstName,
        last_name: payload.lastName,
        role: payload.role,
        practiceGroupId: payload.practiceGroupId,
        activePracticeId: payload.activePracticeId,
      });
      setIsAuthenticated(true);
    } catch {
      throw new Error('Invalid SSO token');
    }
  };

  // Select context (step 2 of two-step login)
  const selectContext = async (practiceGroupId, role, practiceId) => {
    const result = await authService.selectContext(practiceGroupId, role, practiceId);

    if (result.success) {
      setUser(result.user);
      setIsAuthenticated(true);
      setPendingContextSelection(null);
      return { success: true };
    }

    return { success: false, error: result.error };
  };

  // Switch practice within current group (re-issues JWT)
  const switchPractice = async (practiceId) => {
    const result = await authService.switchPractice(practiceId);

    if (result.success) {
      // Refresh user session context after practice switch
      const validateResult = await authService.validateToken();
      if (validateResult.success) {
        setUser(validateResult.user);
      }
      return { success: true };
    }

    return { success: false, error: result.error };
  };

  // Complete setup function
  const completeSetup = async (setupData) => {
    const result = await authService.completeSetup(setupData);

    if (result.success) {
      setSetupRequired(false);
      return { success: true };
    }

    return { success: false, error: result.error, errors: result.errors };
  };

  // Refresh user data
  const refreshUser = async () => {
    const result = await authService.validateToken();
    if (result.success) {
      setUser(result.user);
    }
    return result;
  };

  // Check if user has specific role (uses active role from JWT context)
  const hasRole = (role) => {
    return user?.user?.role === role || user?.activeContext?.role === role;
  };

  // Check if user is super admin
  const isSuperAdmin = () => {
    return user?.user?.role === 'super_admin' || user?.isSuperAdmin;
  };

  // Check if user is practice admin
  const isPracticeAdmin = () => {
    const role = user?.activeContext?.role || user?.user?.role;
    return role === 'practice_admin';
  };

  // Check if user is clinical admin
  const isClinicalAdmin = () => {
    const role = user?.activeContext?.role || user?.user?.role;
    return role === 'clinical_admin';
  };

  // Check if user is care provider
  const isCareProvider = () => {
    const role = user?.activeContext?.role || user?.user?.role;
    return role === 'care_provider';
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    const role = user?.activeContext?.role || user?.user?.role;
    if (!role) return false;
    const allowed = PERMISSIONS[permission];
    if (!allowed) return false;
    return allowed.includes(role);
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    setupRequired,
    pendingContextSelection,
    showTimeoutWarning,
    login,
    loginWithToken,
    logout,
    dismissTimeoutWarning,
    selectContext,
    switchPractice,
    completeSetup,
    refreshUser,
    hasRole,
    isSuperAdmin,
    isPracticeAdmin,
    isClinicalAdmin,
    isCareProvider,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
